<template>
  <div>
    <template v-if="existingContact">
      <contact-already-exist-block :contact="existingContact" />

      <hr class="mt-6 mb-6" />
    </template>

    <v-alert
      v-if="errorMessages.length > 0"
      type="error"
      dismissible
      @input="clearErrors"
      class="mb-4"
    >
      <div v-for="(error, index) in errorMessages" :key="index">
        {{ error }}
      </div>
    </v-alert>

    <v-fade-transition>
      <contact-form-fields
        v-if="contactForm"
        ref="contactFormContainer"
        :contact="contactForm"
        :editableFields="editableFields"
        :restore-empty-value-by-field="restoreEmptyValueByField"
      />
    </v-fade-transition>

    <portal to="createContactAction">
      <v-btn
        :color="modal ? 'appMain' : 'primary'"
        :loading="contactLoading || updating"
        :disabled="disabled"
        @click="saveContact"
      >
        Create
      </v-btn>
    </portal>

    <div
      class="spinner-overlay"
      v-if="contactLoading || updating || !fieldsLoaded"
    >
      <spinner />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import clone from 'lodash/clone';

import { getContactName } from '@/filters/helpers';
import ContactFormFields from '../components/ContactFormFields';
import ContactAlreadyExistBlock from '@/views/contacts/components/ContactAlreadyExistBlock.vue';

export default {
  name: 'ContactForm',
  components: {
    ContactFormFields,
    ContactAlreadyExistBlock,
  },
  props: {
    returnContact: Boolean,
    modal: Boolean,
    search: String,
    disabled: Boolean,
    editableFields: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      contactForm: null,
      existingContact: null,
      updating: false,
      errorMessages: [],
    };
  },
  computed: {
    ...mapGetters([
      'contactLoading',
      'profilePid',
      'profile',
      'roleRelationField',
    ]),
    ...mapGetters('fields', {
      fields: 'all',
      fieldsLoaded: 'loaded',
      getSingleField: 'getSingleField',
    }),

    // When in portal, pass default value for the role relation field so the list field
    // can restore it on blur when empty and keep it as a selectable option when searching.
    restoreEmptyValueByField() {
      if (
        !this.profilePid ||
        !this.roleRelationField ||
        !this.profile?.con_id
      ) {
        return {};
      }
      return {
        [this.roleRelationField]: [
          {
            value: this.profile.con_id,
            text: getContactName(this.profile),
          },
        ],
      };
    },
  },
  watch: {
    fieldsLoaded(newVal) {
      if (newVal) {
        this.setContactForm();
      }
    },
    contactForm: {
      deep: true,
      handler() {
        if (this.existingContact) {
          this.existingContact = null;
        }
        if (this.errorMessages.length > 0) {
          this.clearErrors();
        }
      },
    },
  },
  methods: {
    ...mapActions(['createContact', 'loadSingleContactShort']),
    ...mapActions('fields', { loadFields: 'load' }),
    isFormValid() {
      return this.$refs.contactFormContainer.validate();
    },

    clearErrors() {
      this.errorMessages = [];
    },

    isFieldEditable({ name, form_hidden }) {
      if (form_hidden) return false;
      if (this.editableFields.length === 0) return true;
      return this.editableFields.includes(name);
    },

    getContactTemplate() {
      const fieldsObj = this.fields.find((i) => i.type === 'default')['fields'];
      const fieldsArr = Object.values(fieldsObj)
        .sort((a, b) => a.order - b.order)
        .filter((i) => this.isFieldEditable(i));

      const isListLikeType = (type) =>
        ['list', 'related_contacts', 'relation'].includes(type);

      return fieldsArr.reduce((acc, i) => {
        let defaultValue = isListLikeType(i.type) ? [] : null;

        // Portal create: when the editable field is the role's relation field,
        // default it to the current portal user so they appear as the related contact.
        // Use object with value + text so the field shows the current user's name as label.
        if (
          this.profilePid &&
          this.roleRelationField &&
          i.name === this.roleRelationField &&
          this.profile?.con_id
        ) {
          defaultValue = [
            {
              value: this.profile.con_id,
              text: getContactName(this.profile),
            },
          ];
        }

        acc[i.name] = defaultValue;
        return acc;
      }, {});
    },

    setContactForm() {
      if (this.fieldsLoaded) {
        this.contactForm = clone(this.getContactTemplate());
      }

      if (this.search && this.contactForm) {
        let [firstName, lastName] = this.search.trim().split(' ');
        this.contactForm.first_name = firstName || '';
        this.contactForm.last_name = lastName || '';
      }
    },

    clearData() {
      for (let key in this.contactForm) {
        this.contactForm[key] = Array.isArray(this.contactForm[key]) ? [] : '';
      }

      // Re-apply portal relation default so "Add Another Contact" shows current user again
      if (
        this.profilePid &&
        this.roleRelationField &&
        this.profile?.con_id &&
        this.contactForm.hasOwnProperty(this.roleRelationField)
      ) {
        this.contactForm[this.roleRelationField] = [
          {
            value: this.profile.con_id,
            text: getContactName(this.profile),
          },
        ];
      }

      this.$refs.contactFormContainer.reset();
    },

    getFormData() {
      let formData = Object.fromEntries(
        Object.entries(this.contactForm).filter(([_, value]) => {
          return value || value === false;
        })
      );

      for (let key in formData) {
        const field = this.getSingleField(key);
        if (field.type === 'related_custom_object') {
          formData[`${key}_id`] = formData[key];
          delete formData[key];
        }
        // Normalize relation/related_contacts to array of IDs for API (value may be objects with value/text)
        if (
          field &&
          ['related_contacts', 'relation'].includes(field.type) &&
          Array.isArray(formData[key])
        ) {
          formData[key] = formData[key].map((item) =>
            item && typeof item === 'object' ? item.value ?? item.id : item
          );
        }
      }

      return formData;
    },

    async saveContact() {
      if (!this.isFormValid()) {
        return;
      }

      this.clearErrors();
      this.updating = true;

      const isContactExists = await this.isContactAlreadyExists();
      if (isContactExists) {
        this.updating = false;
        return false;
      }

      try {
        const contact = await this.createContact(this.getFormData());
        this.$emit('created', contact);
      } catch (e) {
        const errorData = e?.data?.data;

        if (errorData?.[0]?.messages) {
          Object.values(errorData[0].messages).forEach((msgs) => {
            this.errorMessages.push(...msgs);
          });
        } else {
          this.errorMessages.push(
            e?.response?.data?.message || 'Failed to create contact'
          );
        }
      } finally {
        this.updating = false;
      }
    },

    async isContactAlreadyExists() {
      this.existingContact = await this.findExistingContact();
      return !!this.existingContact;
    },

    async findExistingContact() {
      const searchableFields = ['mobile', 'email'];

      if (searchableFields.every((f) => !this.contactForm[f])) {
        return null;
      }

      let filters = { $or: [] };
      searchableFields.forEach((f) => {
        if (this.contactForm[f]) {
          const { name } = this.getSingleField(f) || {};
          filters.$or.push({
            [name]: {
              $in: this.contactForm[f],
            },
          });
        }
      });

      return await this.loadSingleContactShort({ filters });
    },
  },
  mounted() {
    this.loadFields({ checkLoaded: true });
    this.setContactForm();
  },
};
</script>
